import template from './answering_queue.html';
import { MLS_TYPE_LABEL_MAP } from '../../../components/UniversalSearch/constants';

class AnsweringQueueHostController {
  static getUrgency(appointment) {
    const currentMoment = moment();
    const notice = appointment.notice || 0;
    if (appointment.status === 'time_passed') return 0;
    if (appointment.flagged) return 1;
    if (
      moment(appointment.time).diff(currentMoment) <
      (3 + notice) * 60 * 60 * 1000
    )
      return 1;
    return 10;
  }

  static setUrgency(appointment) {
    appointment.urgency = AnsweringQueueHostController.getUrgency(appointment);
  }

  static makeSearchFilter = searchQuery => appointmentData => {
    return (
      (appointmentData.listing.line1 &&
        appointmentData.listing.line1
          .toLowerCase()
          .includes(searchQuery.toLowerCase())) ||
      (appointmentData.listing.line2 &&
        appointmentData.listing.line2
          .toLowerCase()
          .includes(searchQuery.toLowerCase())) ||
      (appointmentData.appointment.coop.name &&
        appointmentData.appointment.coop.name
          .toLowerCase()
          .includes(searchQuery.toLowerCase()))
    );
  };

  constructor(
    $q,
    $scope,
    $rootScope,
    $location,
    $routeParams,
    AnsweringService,
    ToastService,
  ) {
    this.$q = $q;
    this.$scope = $scope;
    this.$location = $location;
    this.$rootScope = $rootScope;
    this.$routeParams = $routeParams;

    this.viewDashAppointment = $rootScope.viewDashAppointment;
    this.auth = $rootScope.auth;
    this.AnsweringService = AnsweringService;
    this.searchValue = '';
    this.mlsOptions = [];
    this.filteredMlsOptions = {};
    this.MAX_SERVICE_APPTS = 6;

    this.dataLoaded = false;
    this.data = {};
    this.selected = {};
    this.viewing = {};
    this.filters = {};
    this.maxCount = 0;
    this.pageCount = 0;
    this.currentPage = 0;
    /** @type Record<string, true | undefined> */
    this.allGroupedOrgsMap = {};

    this.isLoading = true;
    this.queuePreferencesModalVisible = false;

    // Triggers a re-render based on isBeingViewed if we update the viewing prop.
    this.$scope.$on('viewing_appointment_changed', (events, viewingState) => {
      this.$scope.$evalAsync(() => {
        this.viewing = Object.entries(viewingState).reduce(
          (acc, [id]) => ({ ...acc, [id]: true }),
          {},
        );
      });
    });

    // Re-update flagged appointment when this event is received.
    this.$scope.$on(
      'appointment_flagged',
      (events, { appointmentId, flagged }) => {
        const data = this.data[appointmentId];
        data.appointment.flagged = flagged;
        AnsweringQueueHostController.setUrgency(data);
        this.$scope.$evalAsync(() => {
          this.data = { ...this.data, [appointmentId]: data };
        });
      },
    );

    this.$scope.$on('appointment_status', (events, entry) => {
      this.$scope.$evalAsync(() => {
        const updatedAppointment = entry.appointment;

        AnsweringQueueHostController.setUrgency(updatedAppointment);

        const { appointmentPreferences, groupPreferences } =
          this.queuePreferences || {};

        let shouldAdd = false;
        if (groupPreferences) {
          Object.values(groupPreferences).forEach(preference => {
            if (preference[updatedAppointment.orgId]) {
              shouldAdd =
                preference[updatedAppointment.orgId][
                  updatedAppointment.listing.location
                ];
            }
          });
        }

        // New AnsweringService - Agent Subscription
        // The org isn't listed in group preferences or the LA has a user-level subscription
        // In either case, we default add it to the queue
        if (!this.allGroupedOrgsMap[updatedAppointment.orgId]) {
          shouldAdd = true;
        }

        if (
          shouldAdd &&
          appointmentPreferences?.[updatedAppointment.status] &&
          updatedAppointment.isPending &&
          !updatedAppointment.isDeleted
        ) {
          this.data = {
            ...this.data,
            [entry.appointmentId]: {
              listing: updatedAppointment.listing,
              appointment: updatedAppointment,
            },
          };
        } else {
          delete this.data[entry.appointmentId];
          this.data = { ...this.data };
        }
      });
    });

    this.$scope.$on('answering_settings_toggled', () => {
      // Reload appointments
      this.onPageChange(this.currentPage);
      ToastService.add({
        message:
          'Appointments are reloaded due to changes to brokerage time settings',
        type: 'warning',
        removeAfter: 120,
      });
    });

    this.$scope.$on('appointment_covid19_signed', (events, entry) => {
      const { appointmentId } = entry;
      const data = this.data[appointmentId];
      const { covidPreferences } = this.queuePreferences;

      if (
        data &&
        data.appointment.covid19RequestedAck &&
        covidPreferences.covid19Signed
      ) {
        data.appointment.covid19Signed = true;
        this.$scope.$apply(() => {
          this.data = {
            ...this.data,
            [appointmentId]: data,
          };
        });
      }
    });

    this.loadMlsTypes();
    this.loadAppointments(
      this.currentPage,
      null,
      window.location.hash
        .split('?selectedMls=')[1]
        ?.split(',')
        .filter(Boolean)
        .reduce((acc, mls) => {
          acc[mls] = true;
          return acc;
        }, {}),
    );
  }

  changePage = (page, search) => {
    this.$scope.$evalAsync(() => {
      this.currentPage = Math.max(0, page - 1); // page is 1-indexed
      this.searchValue = search;
      this.isLoading = true;
      this.loadAppointments(
        this.currentPage,
        this.searchValue,
        this.filteredMlsOptions,
      );
    });
  };

  loadMlsTypes = () => {
    this.AnsweringService.getMlsTypes().then(options => {
      const databaseOptions = options.reduce((acc, curr) => {
        acc.push({
          label: MLS_TYPE_LABEL_MAP[curr] || curr,
          value: curr,
        });
        return acc;
      }, []);
      this.mlsOptions = databaseOptions;
      this.filteredMlsOptions = databaseOptions.reduce((acc, { value }) => {
        acc[value] = false;
        return acc;
      }, {});
      const selectedMlsParams = window.location.hash
        .split('?selectedMls=')[1]
        ?.split(',')
        .filter(Boolean);
      if (selectedMlsParams?.length) {
        this.filteredMlsOptions = selectedMlsParams.reduce(
          (acc, curr) => {
            acc[curr] = true;
            return acc;
          },
          { ...this.filteredMlsOptions },
        );
      }
    });
  };

  loadAppointments = (page, search, mlsFilter) => {
    const mlsTypes =
      mlsFilter &&
      Object.entries(mlsFilter)
        .filter(([, value]) => value)
        .map(([key]) => key);
    return this.$q
      .all([
        this.AnsweringService.loadResource({
          page,
          search,
          ...(Boolean(mlsTypes?.length) && { mlsTypes }),
        }),
        this.AnsweringService.getCentralizedQueuePreferencesState(),
      ])
      .then(([result, preferencesState]) => {
        const data = {};
        result.data.forEach(datum => {
          AnsweringQueueHostController.setUrgency(datum.appointment);
          data[datum.appointment.id] = datum;
        });

        this.$scope.$evalAsync(() => {
          // We can not just push data to the object, but we need to maintain
          // immutability of the reference for react2angular to properly
          // update.
          this.data = data;

          this.preferencesState = preferencesState;

          this.maxCount = result.maxCount;
          this.pageCount = result.count;
          this.isLoading = false;
        });
      });
  };

  getObjectKeys = obj => Object.keys(obj);

  selectedAppointmentCount = () =>
    Object.keys(this.selected).filter(key => this.selected[key]).length;

  onAppointmentSelected = sortedData => {
    this.$scope.$evalAsync(() => {
      const selected = {};
      sortedData.forEach(({ appointment }) => {
        selected[appointment.id] = true;
      });
      this.selected = selected;
      this.firstAppointment =
        sortedData.length > 0 ? sortedData[0].appointment : undefined;
    });
  };

  onPageChange = page => {
    this.changePage(page, this.searchValue);
  };

  onFilteredCountChanged = _count => {
    // ENG-12448 Reverting but leaving this handler for future use
  };

  onAppointmentFlagged = (appointment, newStatus) => {
    this.$scope.$evalAsync(() => {
      // We update the appointment on our side first for UI purposes.
      // Another update once the websocket has been received will
      // re-set the status to what was persisted on the backend.
      const data = this.data[appointment.id];
      data.appointment.flagged = newStatus;
      AnsweringQueueHostController.setUrgency(data);
      // Need to maintain immutability for react2angular to properly
      // update.
      this.data = { ...this.data, [appointment.id]: data };
    });

    // Persist on the backend.
    this.AnsweringService.flagAppointment(appointment.id, newStatus);
  };

  onSort = sortedData => {
    this.$scope.$evalAsync(() => {
      const selected = {};
      sortedData.forEach(({ appointment }) => {
        selected[appointment.id] = true;
      });
      this.selected = selected;
      this.firstAppointment =
        sortedData.length > 0 ? sortedData[0].appointment : undefined;
    });
  };

  onFilterQuery = query => {
    this.$scope.$evalAsync(() => {
      const [searchValue] = Object.values(query);

      // manually call the event handler
      this.searchValue = searchValue;
      this.changePage(0, this.searchValue);
    });
  };

  /**
   * @param {import('@bb/domain/dist/types/CentralizedQueuePreferences').CentralizedQueuePreferences} queuePreferences
   * @param {Record<string, true | undefined>} allGroupedOrgsMap
   */
  onFetchPreferences = (queuePreferences, allGroupedOrgsMap) => {
    this.$scope.$evalAsync(() => {
      this.queuePreferences = queuePreferences;
      this.allGroupedOrgsMap = allGroupedOrgsMap;
    });
  };

  closeQueuePreferencesModal = () => {
    this.$scope.$evalAsync(() => {
      this.queuePreferencesModalVisible = false;
    });
  };

  openQueuePreferencesModal = () => {
    this.$scope.$evalAsync(() => {
      this.queuePreferencesModalVisible = true;
    });
  };

  onResetPreferences = () => {
    this.onSavePreferences();
  };

  onSavePreferences = () => {
    this.$scope.$evalAsync(() => {
      this.closeQueuePreferencesModal();
      this.isLoading = true;
      this.loadAppointments(
        this.currentPage,
        this.searchValue,
        this.filteredMlsOptions,
      );
    });
  };

  serviceAppointments = () => {
    if (this.firstAppointment) {
      this.$rootScope.viewDashAppointment(this.firstAppointment, {
        sids: Object.keys(this.selected),
      });
    }
  };

  clearFilter = () => {
    if (!this.searchValue) return;
    this.$scope.$evalAsync(() => {
      this.searchValue = '';
      this.changePage(0);
    });
  };

  setFilteredMlsOptions = options => {
    this.$scope.$apply(() => {
      const selectedOptions = Object.keys(options).filter(key => options[key]);
      window.history.replaceState(
        null,
        null,
        `#/answering/queue?selectedMls=${selectedOptions.join(',')}`,
      );
      this.filteredMlsOptions = options;
      this.currentPage = 0;
      this.isLoading = true;
      this.loadAppointments(
        this.currentPage,
        this.searchValue,
        this.filteredMlsOptions,
      );
    });
  };
}

angular.module('bb.answering').component('bbAnsweringQueueHost', {
  controller: AnsweringQueueHostController,
  controllerAs: 'vm',
  template,
});

AnsweringQueueHostController.$inject = [
  '$q',
  '$scope',
  '$rootScope',
  '$location',
  '$routeParams',
  'AnsweringService',
  'ToastService',
];
